/**
 * @file: Lead.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * Frontend domain types for the lead-management surface. Mirrors the backend
 * `lead` wire shape (camelCased JSON). String-literal unions match the
 * server's varchar enum values 1:1.
 */

export type LeadStatus =
  | 'pending'
  | 'due'
  | 'working'
  | 'booked'
  | 'converted'
  | 'lost'
  | 'cancelled'
  | 'expired';

export type LeadType = 'acquisition' | 'recurring' | 'win_back' | 'mixed';

export type LeadSource = 'manual' | 'auto';

export type LeadCoverage = 'uncovered' | 'covered' | 'consumed';

/** How the lead entered the system (mirrors the backend `LeadOrigin` varchar). */
export type LeadOrigin =
  | 'system'
  | 'direct_call'
  | 'online_contact'
  | 'walk_in'
  | 'referral'
  | 'campaign'
  | 'other';

/** Who may see the lead (mirrors the backend `LeadVisibility` varchar). */
export type LeadVisibility = 'public' | 'restricted' | 'private';

/** Visibility labels: public = anyone; restricted = managers + owner/creator;
 *  private = owner/creator only. */
export const LeadVisibilityLabel: Record<LeadVisibility, string> = {
  public: '公开',
  restricted: '受限',
  private: '私密',
};

/** Engagement intention; `undetermined` is the unset/default state. */
export type LeadIntention = 'high' | 'medium' | 'low' | 'none' | 'undetermined';

/** Visit-intention (到店意向) labels; `undetermined` is the unset/auto-pending state. */
export const LeadIntentionLabel: Record<LeadIntention, string> = {
  high: '高意向',
  medium: '中意向',
  low: '低意向',
  none: '无意向',
  undetermined: '未确定',
};

/** Card subtitle copy for the lead's origin (线索来源). */
export const LeadOriginLabel: Record<LeadOrigin, string> = {
  system: '系统导入',
  direct_call: '来电咨询',
  online_contact: '在线咨询',
  walk_in: '到店',
  referral: '转介绍',
  campaign: '活动',
  other: '其他',
};

/** Terminal states a lead cannot leave (used to gate actions). */
export const TerminalLeadStatuses: ReadonlySet<LeadStatus> =
  new Set<LeadStatus>(['converted', 'lost', 'cancelled', 'expired']);

export const isTerminalLeadStatus = (status: LeadStatus): boolean =>
  TerminalLeadStatuses.has(status);

export const LeadStatusLabel: Record<LeadStatus, string> = {
  pending: '待跟进',
  due: '需跟进',
  working: '跟进中',
  booked: '已预约',
  converted: '已转化',
  lost: '已流失',
  cancelled: '已取消',
  expired: '已过期',
};

export const LeadTypeLabel: Record<LeadType, string> = {
  acquisition: '拓客',
  recurring: '复购',
  win_back: '挽回',
  mixed: '混合',
};

export const LeadSourceLabel: Record<LeadSource, string> = {
  manual: '手动',
  auto: '自动',
};

export interface Lead {
  id: string;
  orgId: number;
  storeId: number;
  customerId?: string | number | null;
  customerName?: string | null;
  customerPhone?: string | null;
  licensePlateNo: string;
  vin: string;
  currentMileage: number;
  vehicleName: string;
  vehicleBrandName: string;
  vehicleManufacturer?: string | null;
  vehicleModel: string;
  vehicleModelYear?: number | null;
  vehicleDisplacement?: string | null;
  vehicleTrimLevel?: string | null;
  vehicleFuelType?: string | null;
  assigneeId: number;
  assigneeName: string;
  status: LeadStatus;
  type: LeadType;
  source: LeadSource;
  origin: LeadOrigin;
  visibility: LeadVisibility;
  intention: LeadIntention;
  // Set once the lead books a reservation; `reservationDate` is the chosen
  // calendar day (yyyy-mm-dd) and `reservedAt` the booking timestamp.
  reservationId?: string | null;
  reservationDate?: string | null;
  reservedAt?: string | Date | null;
  targetServiceDate: string;
  outreachWindowStart: string | Date;
  expiryDate: string;
  outcomeReason?: string | null;
  recycledFromLeadId?: string | null;
  convertedOrderNo?: string | null;
  // Denormalized activity pointers (see backend migration 01.00.1165). `reach`
  // is the last non-note outreach (a real contact); `activity` is the latest
  // entry of any kind. `*Meta` is the typed LeadActivityData JSON the client
  // renders from. Surfaced so the list shows the last touch without a join.
  lastReachId?: string | null;
  lastReachAt?: string | Date | null;
  lastReachMeta?: string | null;
  lastActivityId?: string | null;
  lastActivityAt?: string | Date | null;
  lastActivityMeta?: string | null;
  createdAt: string | Date;
  createdBy: number;
  createdByName: string;
  updatedAt?: string | Date | null;
  updatedBy?: number | null;
  updatedByName?: string | null;
}
